import { blockMediaRef, formPages } from '@ayumia/component-runtime';
import { useState } from 'react';
import type { ApiClient } from '../../../api/client';
import { text } from '../../../app/i18n';
import { Icon } from '../../../design-system/icons';
import { aggregatesOf, blocksOf, setBlockBinding, setBlockProp, type Template } from '../layout-model';
import { Line, Sub } from './controls';
import { MediaPicker, OPTION_ICONS } from './MediaPicker';

/**
 * Réglages des blocs d'affichage et d'action de l'éditeur v2 (ADR-146 addendum (20)) : Image (« Choisir dans
 * la médiathèque »), Bouton d'action (action en phrases, liste ou page visée), Puce de compteur (total déclaré).
 * Population non informaticienne : ni JSON, ni identifiant, ni chemin ; des phrases et des listes.
 */

type Apply = (next: Template, coalesce?: string) => void;
type Localized = Readonly<Record<string, string>>;

/** Blocs dont les réglages sont rendus ici plutôt que par les champs génériques de l'inspecteur. */
export const DISPLAY_BLOCKS: ReadonlySet<string> = new Set(['FormImage', 'ActionButton', 'CounterChip']);
/** Propriétés réglées ici (les textes multilingues restent à l'onglet « Textes »). */
export const DISPLAY_BLOCK_PROPS: ReadonlySet<string> = new Set(['image', 'action', 'section', 'page', 'icon']);

const ACTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'next_page', label: 'Page suivante' },
  { value: 'previous_page', label: 'Page précédente' },
  { value: 'submit', label: 'Valider la fiche' },
  { value: 'add_row', label: 'Ajouter une ligne' },
  { value: 'open_page', label: 'Ouvrir une page' },
];

const seriesOf = (template: Template) => template.sections.filter((section) => section.series !== undefined);

/**
 * Réglages posés à l'insertion d'un bloc pour qu'il soit tout de suite valide quand c'est possible : le
 * bouton ajoute une ligne à la première liste (sinon « Page suivante »), la puce montre le premier total.
 * L'image n'a pas de valeur par défaut : l'auteur la choisit dans la médiathèque.
 */
export function withDisplayDefaults(template: Template, name: string, blockId: string): Template {
  if (name === 'ActionButton') {
    const list = seriesOf(template)[0];
    const next = setBlockProp(template, blockId, 'action', list === undefined ? 'next_page' : 'add_row');
    return list === undefined ? next : setBlockProp(next, blockId, 'section', list.section_id);
  }
  if (name === 'CounterChip') {
    const first = aggregatesOf(template)[0];
    return first === undefined ? template : setBlockBinding(template, blockId, 'value', `$aggregates.${first.aggregate_id}`);
  }
  return template;
}

export function DisplayBlockSettings({ name, template, blockId, apply, client }: { name: string; template: Template; blockId: string; apply: Apply; client: ApiClient | undefined }) {
  const [picking, setPicking] = useState(false);
  const block = blocksOf(template).find((candidate) => candidate.block_id === blockId);
  if (block === undefined) return null;
  const props = (block.props ?? {}) as Readonly<Record<string, unknown>>;
  const icon = typeof props['icon'] === 'string' ? props['icon'] : undefined;
  const iconChoice = (
    <Line label="Icône" stacked>
      <select aria-label="Icône" value={icon ?? ''} onChange={(event) => apply(setBlockProp(template, blockId, 'icon', event.target.value === '' ? undefined : event.target.value))}>
        <option value="">Sans icône</option>
        {OPTION_ICONS.map((choice) => (
          <option key={choice} value={choice}>
            {choice}
          </option>
        ))}
      </select>
    </Line>
  );

  if (name === 'FormImage') {
    const ref = blockMediaRef(props['image']);
    return (
      <Sub title="Image">
        <Line label="Image">
          <span className="ay-v2-inline">
            {/* La médiathèque s'ouvre depuis la vignette elle-même. */}
            <button type="button" className="ay-v2-visual ay-v2-visual-button" aria-label={ref === undefined ? 'Choisir dans la médiathèque' : 'Changer l’image'} disabled={client === undefined} title={client === undefined ? 'Médiathèque indisponible ici' : ref === undefined ? 'Choisir dans la médiathèque' : 'Changer l’image'} onClick={() => setPicking(true)}>
              <Icon name="image" size={18} />
            </button>
            <span data-testid="block-image-state">{ref === undefined ? 'Aucune' : 'common_key' in ref ? 'Image commune' : 'Médiathèque'}</span>
            {ref !== undefined && (
              <button type="button" className="ay-v2-link" onClick={() => apply(setBlockProp(template, blockId, 'image', undefined))}>
                Retirer
              </button>
            )}
          </span>
        </Line>
        {picking && client !== undefined && (
          <MediaPicker
            client={client}
            title="Image du bloc"
            subtitle="Montrée dans le formulaire, sur le web et la tablette"
            allowIcon={false}
            {...(ref === undefined ? {} : { current: { image: ref } })}
            onClose={() => setPicking(false)}
            onChoose={(choice) => {
              setPicking(false);
              if ('image' in choice) apply(setBlockProp(template, blockId, 'image', { $literal: choice.image }));
            }}
          />
        )}
      </Sub>
    );
  }

  if (name === 'ActionButton') {
    const action = typeof props['action'] === 'string' ? props['action'] : undefined;
    const lists = seriesOf(template);
    const pages = formPages(template);
    return (
      <Sub title="Action">
        <Line label="Quand on appuie" stacked>
          <select
            aria-label="Quand on appuie"
            value={action ?? ''}
            onChange={(event) => {
              const next = event.target.value;
              let updated = setBlockProp(template, blockId, 'action', next === '' ? undefined : next);
              updated = setBlockProp(updated, blockId, 'section', next === 'add_row' ? lists[0]?.section_id : undefined);
              updated = setBlockProp(updated, blockId, 'page', next === 'open_page' ? pages[0]?.page_id : undefined);
              apply(updated);
            }}
          >
            {action === undefined && <option value="">Choisir une action</option>}
            {ACTIONS.map((choice) => (
              <option key={choice.value} value={choice.value} disabled={(choice.value === 'add_row' && lists.length === 0) || (choice.value === 'open_page' && pages.length < 2)}>
                {choice.label}
              </option>
            ))}
          </select>
        </Line>
        {action === 'add_row' && (
          <Line label="Ajouter une ligne à" stacked>
            <select aria-label="Ajouter une ligne à" value={typeof props['section'] === 'string' ? props['section'] : ''} onChange={(event) => apply(setBlockProp(template, blockId, 'section', event.target.value))}>
              {lists.map((list) => (
                <option key={list.section_id} value={list.section_id}>
                  {text(list.label as Localized, 'fr', 'Liste')}
                </option>
              ))}
            </select>
          </Line>
        )}
        {action === 'open_page' && (
          <Line label="Page à ouvrir" stacked>
            <select aria-label="Page à ouvrir" value={typeof props['page'] === 'string' ? props['page'] : ''} onChange={(event) => apply(setBlockProp(template, blockId, 'page', event.target.value))}>
              {pages.map((page) => (
                <option key={page.page_id} value={page.page_id}>
                  {text(page.label as Localized | undefined, 'fr', 'Page')}
                </option>
              ))}
            </select>
          </Line>
        )}
        {iconChoice}
      </Sub>
    );
  }

  if (name === 'CounterChip') {
    const path = block.bind?.['value']?.path;
    const totals = aggregatesOf(template);
    return (
      <Sub title="Total affiché">
        <Line label="Total" stacked>
          <select aria-label="Total" value={path ?? ''} onChange={(event) => apply(setBlockBinding(template, blockId, 'value', event.target.value === '' ? undefined : event.target.value))}>
            <option value="">{totals.length === 0 ? 'Aucun total dans la fiche' : 'Choisir un total'}</option>
            {totals.map((total) => (
              <option key={total.aggregate_id} value={`$aggregates.${total.aggregate_id}`}>
                {text(total.label as Localized | undefined, 'fr', 'Total')}
              </option>
            ))}
          </select>
        </Line>
        {iconChoice}
      </Sub>
    );
  }
  return null;
}

